<template>
    <div class="popular-collections-slider">
        <h2 class="text-2xl font-semibold mb-6">Популярные товары</h2>

        <div class="relative" v-if="!is_loading">
            <Swiper
                :slides-per-view="4"
                :space-between="20"
                :breakpoints="breakpoints"
                :loop="products.length > maxSlidesPerView"
                :autoplay="{ delay: 5000, disableOnInteraction: false }"
                @swiper="setSwiper"
                class="pagination-base long_bullet h-full"
            >
                <SwiperSlide v-for="(product, i) in products" :key="product.id">
                    <!-- оборачиваем слайд, чтобы наблюдать видимость -->
                    <div ref="slideEls" class="slide-observe" :data-id="product.id" :data-pos="i + 1">
                        <CatalogProductItemGrid
                            :data="product"
                            :list-name="listName"
                            :position="i + 1"
                            :default-mode="catalogStore.grid_mods.small"
                            class="min-h-[485px] md:min-h-[512px]"
                        />
                    </div>
                </SwiperSlide>
            </Swiper>

            <div class="flex items-end justify-between max-md:me-5 mt-5">
                <div class="flex gap-3 max-md:hidden">
                    <button v-if="swiper" @click.prevent="slidePrev" aria-label="Назад"
                            class="bg-baseColor md:hover:bg-baseColor/70 rounded-full w-[44px] h-[44px] flex items-center justify-center transition">
                        <IconAngleLeft class="w-5 h-5 text-white"/>
                    </button>
                    <button v-if="swiper" @click.prevent="slideNext" aria-label="Вперед"
                            class="bg-baseColor md:hover:bg-baseColor/70 rounded-full w-[44px] h-[44px] flex items-center justify-center transition">
                        <IconAngleLeft class="w-5 h-5 text-white rotate-180"/>
                    </button>
                </div>
            </div>
        </div>

        <div v-else>
            <div class="h-8 bg-gray-200 animate-pulse rounded w-1/4 mb-4"></div>
            <Swiper :slides-per-view="4" :space-between="30" @swiper="setSwiper"
                    :breakpoints="breakpoints"
                    :autoplay="{ delay: 5000, disableOnInteraction: true }"
                    class="pagination-base long_bullet bg-transparent">
                <SwiperSlide v-for="i in 4" :key="'sk-'+i">
                    <CatalogProductSkeleton variant="grid"/>
                </SwiperSlide>
            </Swiper>
            <div class="flex gap-3 mt-5">
                <div class="rounded-full animate-pulse h-10 w-10 bg-gray-200"></div>
                <div class="rounded-full animate-pulse h-10 w-10 bg-gray-200"></div>
            </div>
        </div>
    </div>
</template>

<script>
import {Swiper, SwiperSlide} from 'swiper/vue'
import {Navigation, Pagination, Autoplay} from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import {Link} from '@inertiajs/vue3'
import axios from 'axios'
import IconAngleLeft from "@js/components/Icons/IconAngleLeft.vue";
import CatalogProductSkeleton from "@js/components/Sections/Catalog/ProductItem/CatalogProductSkeleton.vue";
import {useCatalogStore} from "@js/store/CatalogStore.js";
import CatalogProductItemGrid from "@js/components/Sections/Catalog/ProductItem/CatalogProductItemGrid.vue";
import {mapProduct} from '@js/ecommerce.js'   // для impressions

export default {
    name: 'PopularProducts',
    components: {
        CatalogProductItemGrid,
        CatalogProductSkeleton,
        IconAngleLeft,
        Swiper,
        SwiperSlide,
        Link,
    },
    data() {
        return {
            products: [],
            is_loading: true,
            swiper: null,
            breakpoints: {
                320: {slidesPerView: 1.2, spaceBetween: 10},
                640: {slidesPerView: 2.2, spaceBetween: 15},
                1024: {slidesPerView: 3, spaceBetween: 15},
                1400: {slidesPerView: 4, spaceBetween: 20},
            },

            // e-com
            listName: 'Популярные товары',
            _io: null,
            _sentImpressions: new Set(),  // ключ: id
        }
    },
    computed: {
        maxSlidesPerView() {
            return Math.max(...Object.values(this.breakpoints).map(bp => bp.slidesPerView))
        },
    },
    methods: {
        getData() {
            this.is_loading = true
            axios.post('/axios/products/get-popular')
                .then(({data}) => {
                    this.products = data.priority || []
                })
                .finally(() => {
                    this.is_loading = false
                    this.$nextTick(() => this._observeVisibleSlides())
                })
        },
        setSwiper(swiperInstance) {
            this.swiper = swiperInstance
        },
        slideNext() {
            this.swiper?.slideNext()
        },
        slidePrev() {
            this.swiper?.slidePrev()
        },

        // ====== E-COM: IMPRESSIONS (видимость слайдов) ======
        _dlPush(payload) {
            try {
                window.dataLayer = window.dataLayer || []
                window.dataLayer.push(payload)
            } catch (e) { /* no-op */
            }
        },
        _observeVisibleSlides() {
            // зачистка прежнего наблюдателя
            try {
                this._io?.disconnect?.()
            } catch {
            }
            const els = this.$refs.slideEls || []
            if (!els.length) return

            this._io = new IntersectionObserver((entries) => {
                const toSend = []
                entries.forEach(entry => {
                    if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return
                    const el = entry.target
                    const id = Number(el.dataset.id)
                    const pos = Number(el.dataset.pos) || 1
                    if (!id || this._sentImpressions.has(id)) return

                    // находим товар по id
                    const product = this.products.find(p => Number(p.id) === id)
                    if (!product) return

                    // собираем productFieldObject
                    const pfo = mapProduct(product, {list: this.listName, position: pos})
                    toSend.push(pfo)
                    this._sentImpressions.add(id)
                })

                if (toSend.length) {
                    // можно чанковать, но тут обычно 1-4
                    this._dlPush({
                        ecommerce: {
                            currencyCode: 'RUB',
                            impressions: toSend
                        }
                    })
                }
            }, {threshold: [0.6]})

            els.forEach(el => {
                if (el) this._io.observe(el)
            })
        },
    },
    mounted() {
        this.getData()
        // перестраховка: при ресайзе/смене ориентации переоценим видимость
        window.addEventListener('resize', this._observeVisibleSlides)
    },
    beforeUnmount() {
        try {
            this._io?.disconnect?.()
        } catch {
        }
        window.removeEventListener('resize', this._observeVisibleSlides)
    },
    setup() {
        const catalogStore = useCatalogStore()
        return {modules: [Navigation, Pagination, Autoplay], catalogStore}
    }
}
</script>

<style scoped lang="scss">
/* no-op */
</style>
